import React from "react";

import { filter, identity, isEmpty, isNil, reduce } from "lodash/fp";

import { MeetOpsQueryResult } from "@app/analytics/hooks/metrics/useMetricsQuery";
import { MeetingPolicyMetric } from "@app/analytics/MeetingPolicy/types";
import { getMetricMetaById } from "@app/analytics/meetops/metrics";
import { QueryDimension } from "@app/analytics/meetops/types";

import { Orientation, Series } from "./BarChart/types";

export const MEETING_POLICY_TABS = [
  {
    id: "meetingScore",
    label: "Read Score",
    enabled: true,
  },
  {
    id: "sentiment",
    label: "Sentiment",
    enabled: true,
  },
  {
    id: "engagement",
    label: "Engagement",
    enabled: true,
  },
  {
    id: "onTimeRate",
    label: "On-Time Rate",
    enabled: true,
  },
];

const removeNils = (metric: MeetingPolicyMetric, data: any[]) =>
  filter((d) => !isNil(d[metric].value), data);

export const getBarSeries = <T extends Orientation>(
  orientation: T,
  type: "bar" | "line" | "text",
  domain: string[],
  series: {
    dimension: string;
    value: number | null;
    formattedValue: string | null;
  }[],
  options: {
    fillColor?: (d: number | null) => React.CSSProperties["fill"];
    name: string;
    formatString?: (s: string | null) => string | null;
    formatDimension?: (s: any) => string;
    hideLabels?: boolean;
  }
): Series<T> => {
  const dimensionData = domain.map(options.formatDimension ?? identity);
  const valueData = isEmpty(series)
    ? []
    : domain.map((d) => {
        const target = series.find((s) => s.dimension === d);
        return target?.value ?? null;
      });
  const formatString = options.formatString ?? identity;
  const text = domain.map((d) => {
    const target = series.find((s) => s.dimension === d);
    return formatString(target?.formattedValue ?? null);
  });

  if (orientation === "vertical") {
    return {
      x: dimensionData,
      y: valueData,
      text,
      type,
      ...options,
    } as Series<T>;
  }

  return {
    x: valueData,
    y: dimensionData,
    text,
    type,
    ...options,
  } as Series<T>;
};

export const getWeightedAverage = (
  meetingData: any[],
  metric: MeetingPolicyMetric
) => {
  // filter out any null data value since we consider null values
  // as not measurable.
  const meetingDataNoNils = removeNils(metric, meetingData);

  const sum = reduce(
    (total, data) =>
      total + data[metric].value * (data.meetingCount?.value ?? 0),
    0,
    meetingDataNoNils
  );

  const sumWeights = reduce(
    (total, data) => total + (data.meetingCount?.value ?? 0),
    0,
    meetingDataNoNils
  );

  if (sumWeights == 0) {
    return { value: null, formattedValue: null };
  }
  const value = Math.round(sum / sumWeights);
  const metricMeta = getMetricMetaById(metric);
  return {
    value,
    formattedValue: metricMeta.toFormat(value),
  };
};

export const getSum = (data: any[], metric: MeetingPolicyMetric) => {
  if (!data) return { value: null, formattedValue: null };

  const dataNoNils = removeNils(metric, data);
  const value = dataNoNils.reduce(
    (total: number, next: any) => total + (next[metric]?.value ?? 0),
    0
  );
  const metricMeta = getMetricMetaById(metric);
  return {
    value: metricMeta.toValue(value),
    formattedValue: metricMeta.toFormat(value),
  };
};

export const getSeries = (
  dimension: QueryDimension,
  metric: MeetingPolicyMetric,
  data?: MeetOpsQueryResult
):
  | {
      dimension: string;
      value: number | null;
      formattedValue: string | null;
    }[]
  | null => {
  if (!data) {
    return null;
  }

  const result = data.results[0].data;
  return result.map((item) => ({
    dimension: item[dimension] as string,
    value: item[metric].value as number | null,
    formattedValue: item[metric].formattedValue as string | null,
  }));
};
